import "./tailwind.css";
import "./global.css";
import "swiper/css";

import { withSentry } from "@sentry/remix";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { LoaderFunction, MetaFunction } from "react-router";
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useRouteLoaderData,
} from "react-router";

import { getGtm, getMenuList } from "~/core";
import { getDocuments } from "~/core/deprecated/documents";
import { getLayoutTranslations } from "~/core/services/static-translations.service";
import { filterMenuByTimezone } from "~/core/transformers/menu.transformer";
import { Footer, FooterTranslationProvider } from "~/features/footer";
import { FooterTranslationProviderV2, FooterV2 } from "~/features/footerV2";
import { Header } from "~/features/header/Header";
import { HeaderTranslationProvider } from "~/features/headerV2/HeaderTranslationProvider";
import { HeaderV2 } from "~/features/headerV2/HeaderV2";
import {
  domainsSettings,
  PageInfoProvider,
} from "~/features-deprecated/banner/usePageInfoContext";
import { runAffiliateMiddleware, runLoaderAuthMiddleware } from "~/middlewares";
import { UIKitProvider, useUiKitComponents } from "~/shared/ui-kit";
import { createCssVars } from "~/shared/ui-kit/ui-kit-provider/createCssVars";
import { makePlayNowUrl } from "~/shared/utils/aff-integration";
import { createTheme } from "~/shared/utils/create-theme";
import { createLinkComponent } from "~/shared/utils/createLinkComponent";
import { DirectusProvider } from "~/shared/utils/directus/directus.provider";
import { GtmProvider, useGtmContext } from "~/shared/utils/gtm-sdk/provider";
import { createGtmInstance } from "~/shared/utils/gtm-sdk/provider/createGtmInstance";

export const loader = ((options) => {
  return runAffiliateMiddleware(options, async (args) => {
    return runLoaderAuthMiddleware(
      args,
      async ({
        request,
        context: { featureFlags, sdk, settings, affiliate },
      }) => {
        const url = new URL(request.url);
        const domainSettings = (Object.entries(domainsSettings).find(([key]) =>
          url.host.startsWith(key),
        ) ?? [])[1];

        const [gtm, translations, menus] = await Promise.all([
          sdk.request(getGtm()),
          sdk.request(getLayoutTranslations()),
          sdk.request(
            getMenuList({
              slugs: [
                settings?.layout?.header_menu?.slug
                  ? settings?.layout.header_menu.slug
                  : "",
                settings?.layout?.footer_menu?.slug
                  ? settings?.layout.footer_menu.slug
                  : "",
              ],
            }),
          ),
        ]);

        let headerMenu;
        let footerMenu;

        menus.forEach((menu) => {
          if (menu.slug === "header") {
            headerMenu = filterMenuByTimezone(
              menu,
              settings?.app?.offsetTimezone
                ? Number(settings.app.offsetTimezone)
                : 0,
            );
          } else if (menu.slug === "footer") {
            footerMenu = filterMenuByTimezone(
              menu,
              settings?.app?.offsetTimezone
                ? Number(settings.app.offsetTimezone)
                : 0,
            );
          }
        });

        return {
          domainSettings,
          documents: getDocuments(),
          pageUrl: url,
          featureFlags: {
            isNavigationEnabled: featureFlags.getItem("headerNavigationLinks")
              .enabled,
            isLanguageSelectorEnabled: featureFlags.getItem(
              "headerLanguageSelector",
            ).enabled,
            isCreateButtonEnabled:
              featureFlags.getItem("headerCreateButton").enabled,
            isFooterNavigationLinkEnabled: featureFlags.getItem(
              "footerNavigationLinks",
            ).enabled,
            isMenuConstructorEnabled:
              featureFlags.getItem("menuConstructor").enabled,
            isFooterMenuConstructorEnabled: featureFlags.getItem(
              "footerMenuConstructor",
            ).enabled,
          },
          affiliate,
          gtmId: gtm?.container_identifier,
          translations,
          headerMenu,
          footerMenu,
          cmsPath: process.env.CMS_PUBLIC_PATH,
          sentryDsn: process.env.SENTRY_DSN,
          fontSize: settings.typography,
          fontFamily: settings.font_family,
          fontWeight: settings.font_weight,
          lineHeight: settings.line_height,
        };
      },
    );
  });
}) satisfies LoaderFunction;

export function Layout({ children }: { children: ReactNode }): ReactNode {
  const data = useRouteLoaderData<typeof loader>("root");
  const {
    i18n: { language },
  } = useTranslation();
  const gtmId = data?.gtmId;
  const theme = createTheme(language, {
    fontSize: data?.fontSize ?? undefined,
    lineHeight: data?.lineHeight ?? undefined,
    fontWeight: data?.fontWeight ?? undefined,
    fonFamily: {
      main: data?.fontFamily?.main_name ?? undefined,
      additional: data?.fontFamily?.additional_name ?? undefined,
    },
  });
  return (
    <html lang={language} style={createCssVars(theme)}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="new-version" content="1.7.1" />
        <Meta />

        {data?.fontFamily?.main_name && data?.fontFamily?.main_url && (
          <link rel="stylesheet" href={data?.fontFamily?.main_url} />
        )}
        {data?.fontFamily?.additional_name &&
          data?.fontFamily?.additional_url && (
            <link rel="stylesheet" href={data?.fontFamily?.additional_url} />
          )}
        <Links />

        {gtmId && (
          <script
            suppressHydrationWarning={true}
            dangerouslySetInnerHTML={{
              __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','${gtmId}');`,
            }}
          />
        )}
      </head>

      <body>
        {/* <!-- Google Tag Manager (noscript) --> */}
        {gtmId && (
          <noscript>
            <iframe
              src={`https://www.googletagmanager.com/ns.html?id=${gtmId}`}
              height="0"
              width="0"
              style={{ display: "none", visibility: "hidden" }}
            ></iframe>
          </noscript>
        )}
        {/* <!-- End Google Tag Manager (noscript) --> */}
        <GtmProvider sdk={createGtmInstance()}>
          <UIKitProvider
            value={{
              components: {
                Link: createLinkComponent(language),
              },
              theme,
            }}
          >
            <DirectusProvider url={data?.cmsPath ?? ""}>
              {children}
            </DirectusProvider>
          </UIKitProvider>
        </GtmProvider>
        <ScrollRestoration />
        <Scripts />
        <script
          dangerouslySetInnerHTML={{
            __html: `if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register("/sw.js").then(() => console.info("service woeker was registred")).catch(function(error) {
        console.error("Ошибка регистрации Service Worker:", error);
      });
    }`,
          }}
        />
      </body>
    </html>
  );
}

export const meta: MetaFunction = () => {
  return [{ title: "LuckyPoker 休闲扑克玩家的天堂" }];
};

export type PageLink =
  | {
      slug: string;
      segment?: string | null;
    }
  | null
  | undefined;

function buildLink(page: PageLink): string {
  if (!page?.slug) {
    return "/";
  }

  const { segment, slug } = page;
  if (segment) {
    return `/${segment}/${slug}`;
  } else {
    return `/${slug}`;
  }
}

function App(): ReactNode {
  const {
    t,
    i18n: { language },
  } = useTranslation("footer");
  const { t: tHeader } = useTranslation("header");

  const {
    pageUrl,
    domainSettings,
    featureFlags,
    documents,
    affiliate,
    translations,
    headerMenu,
    footerMenu,
    sentryDsn,
  } = useLoaderData<typeof loader>();

  const { Link } = useUiKitComponents();
  const { gtm } = useGtmContext();
  const headerAnalytics = {
    clickHeaderMain(triggerUrl: string) {
      const triggerId = triggerUrl.replace(/^\/+|\/+$/g, "");
      gtm.clickHeaderMain({
        eventTime: new Date(),
        playerID: null,
        triggerId: triggerId === "promotions" ? "Features" : triggerId,
      });
    },
    clickHeaderSub(targetUrl: string) {
      const targetId =
        targetUrl
          .replace(/^\/|\/$/g, "")
          .split("/")
          .at(-1) ?? "";

      gtm.clickHeaderSub({
        eventTime: new Date(),
        playerID: null,
        targetId,
        // одна из кнопок Хедера в котором кликнули на элемент Выпадающего меню (пока только Features). Аналитика требует название кнопки как на сайте
        triggerId: "Features",
        screenName: "mainPage",
      });
    },
    createAccountHeader() {
      gtm.createAccountHeader({
        eventTime: new Date(),
        playerID: null,
        screenName: "logIn",
      });
    },
    loginHeader() {
      gtm.loginHeader({
        eventTime: new Date(),
        playerID: null,
        screenName: "signUp",
      });
    },
  };

  const footerStaticTranslations = {
    info: {
      rights:
        translations.static_translations?.footer?.[0]?.widget_info_rights ??
        t("widget.info.rights"),
      gamblingAddictive:
        translations.static_translations?.footer?.[0]
          ?.widget_info_gamblingAddictive ??
        t("widget.info.gambling-addictive"),
      playResponsibly:
        translations.static_translations?.footer?.[0]
          ?.widget_info_playResponsibly ?? t("widget.info.play-responsibly"),
      supportDescription:
        translations.static_translations?.footer?.[0]
          ?.widget_info_supportDescription ??
        t("widget.info.support-description"),
      supportEmail:
        translations.static_translations?.footer?.[0]
          ?.widget_info_supportEmail ?? "widget.info.support-email",
    },
  };

  const headerStaticTranslations = {
    buttons: {
      login: tHeader("auth.button.login"),
      create: tHeader("auth.button.create"),
    },
  };

  return (
    <main className="min-h-screen flex flex-col justify-between">
      <span
        id={"sentryDsn"}
        data-value={sentryDsn}
        style={{ position: "absolute" }}
      />
      <div>
        {featureFlags.isMenuConstructorEnabled ? (
          <HeaderTranslationProvider value={headerStaticTranslations}>
            <HeaderV2
              playNowLink={makePlayNowUrl(affiliate)}
              featureFlags={featureFlags}
              analytics={headerAnalytics}
              menuData={headerMenu}
              language={language}
              buildLink={buildLink}
            />
          </HeaderTranslationProvider>
        ) : (
          <Header
            playNowLink={makePlayNowUrl(affiliate)}
            featureFlags={featureFlags}
            analytics={headerAnalytics}
          />
        )}
        <PageInfoProvider url={pageUrl} domainSettings={domainSettings}>
          <Outlet />
        </PageInfoProvider>
      </div>

      {featureFlags.isFooterMenuConstructorEnabled ? (
        <FooterTranslationProviderV2 value={footerStaticTranslations}>
          <FooterV2
            featureFlags={featureFlags}
            language={language}
            menuData={footerMenu}
            buildLink={buildLink}
          />
        </FooterTranslationProviderV2>
      ) : (
        <FooterTranslationProvider value={footerStaticTranslations}>
          <Footer
            renderLink={(url, content) => <Link href={url}>{content}</Link>}
            featureFlags={featureFlags}
            documents={documents}
            language={language}
          />
        </FooterTranslationProvider>
      )}
    </main>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export default withSentry(App);
